import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Suspense, lazy } from "react";
import "react-toastify/dist/ReactToastify.css";

// Critical components (loaded immediately)
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import LoadingSpinner from "./components/LoadingSpinner"; // Create this component
import ScrollToTop from "./components/ScrollToTop";

// Context providers
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { MetalRatesProvider } from "./context/MetalRatesContext";

// Lazy load all pages
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const SubCategories = lazy(() => import("./pages/SubCategories"));
const ProductList = lazy(() => import("./pages/ProductList"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Profile = lazy(() => import("./pages/Profile"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Search = lazy(() => import("./pages/Search"));
const Admin = lazy(() => import("./pages/Admin"));

// Lazy load admin pages
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const AdminSubcategories = lazy(() => import("./pages/admin/Subcategories"));
const AdminBanners = lazy(() => import("./pages/admin/Banners"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));

function App() {
  console.log("new version 1.0");
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <CartProvider>
          <MetalRatesProvider>
            <WishlistProvider>
              <div className="flex flex-col min-h-screen">
                <Header />
                <main className="flex-grow">
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/product/:id" element={<ProductDetails />} />
                      <Route
                        path="/category/:categoryId"
                        element={<SubCategories />}
                      />
                      <Route
                        path="/products/:categoryId/:subCategoryId"
                        element={<ProductList />}
                      />
                      <Route path="/products" element={<ProductList />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />

                      {/* Protected Routes */}
                      <Route
                        path="/wishlist"
                        element={
                          <ProtectedRoute>
                            <Wishlist />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin Routes */}
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <Admin />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/products"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <AdminProducts />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/categories"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <AdminCategories />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/subcategories"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <AdminSubcategories />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/banners"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <AdminBanners />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <AdminUsers />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/settings"
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <AdminSettings />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                </main>
                <Footer />
              </div>
              <ToastContainer position="top-right" autoClose={3000} />
            </WishlistProvider>
          </MetalRatesProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
